import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import {
  Typography,
  Container,
  Box,
  Divider,
  Button,
  IconButton,
  Tabs,
  Tab,
  AppBar,
  Link as MuiLink,
  Menu,
  MenuItem,
  Fade
} from "@mui/material";

import InstagramIcon from '@mui/icons-material/Instagram';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import AboutUs from "./components/AboutUs.js";
import Glossary from "./components/Glossary.js";
import SearchForm from "./components/SearchForm";
import ImageGallery from "./components/ImageGallery";
import { postSearchQuery } from "./services/api";

const POPULAR_CARDS = [
  "Jeska's Will", "Smothering Tithe", "Mystic Remora", "Green Sun's Zenith",
  "Dark Ritual", "Impact Tremors", "Blood Artist", "Reanimate", "Doubling Season"
];

const NavigationHeader = () => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const currentTab = () => {
    if (location.pathname === "/" || location.pathname.startsWith("/search")) return 0;
    if (location.pathname === "/glossary") return 1;
    return false;
  };

  return (
    <AppBar position="sticky" sx={{ backgroundColor: "#000", borderBottom: "1px solid #333", elevation: 0, backgroundImage: "none", boxShadow: "none" }}>
      {/* Wrapper fluido al 100% senza blocchi rigidi esterni */}
      <Box sx={{ width: "100%" }}>
        <Tabs
          value={currentTab()}
          indicatorColor="secondary"
          alignItems="centre"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile

          sx={{
            width: "100%",
            "& .MuiTabs-flexContainer": {
              justifyContent: {
                xs: "flex-start", 
                md: "center"      
              }
            },
            "& .MuiTab-root": {
              color: "#aaa",
              fontWeight: "bold",
              py: 2,
              px: 2,                   
              fontSize: "0.9rem",     
              whiteSpace: "nowrap",    
              minWidth: "auto",        
            },
            "& .Mui-selected": { color: "#b88aff !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#b88aff", height: "3px" },
            // Colore delle freccette di scorrimento che appaiono sui telefoni molto piccoli
            "& .MuiTabs-scrollButtons": {
              color: "#b88aff",
              "&.Mui-disabled": { opacity: 0.3 }
            }
          }}
        >
          <Tab label="Card Search" component={Link} to="/" sx={{ textTransform: "none" }} />
          <Tab label="Keywords" component={Link} to="/glossary" sx={{ textTransform: "none" }} />
          <Tab label="Suggest a Card" component={Link} to="https://docs.google.com/forms/d/e/1FAIpQLSdhxoG76b_W5kQwaMXN10SEM9zxczMDcbEnzMg_od3M_26_Ew/viewform?usp=header" sx={{ textTransform: "none" }} />
          <Tab
            component="div"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  id="most-searched-button"
                  aria-controls={open ? 'most-searched-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: open ? "#b88aff" : "#aaa",
                    fontWeight: "bold",
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    fontSize: "0.85rem", 
                    "&:hover": { color: "#b88aff" }
                  }}
                >
                  Most Searched
                </Button>

                <Menu
                  id="most-searched-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  TransitionComponent={Fade}
                  disableScrollLock={true}
                  sx={{
                    "& .MuiPaper-root": {
                      backgroundColor: "#111",
                      border: "1px solid #333",
                      color: "#f2f2f2",
                      mt: 1,
                      minWidth: 180,
                    }
                  }}
                >
                  {POPULAR_CARDS.map((card) => (
                    <MenuItem
                      key={card}
                      component={Link}
                      to={`/to/${encodeURIComponent(card)}`}
                      onClick={handleMenuClose}
                      sx={{
                        fontSize: "0.9rem",
                        "&:hover": { backgroundColor: "#b88aff22", color: "#b88aff" }
                      }}
                    >
                      {card}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            }
          />
        </Tabs>
      </Box>
    </AppBar>
  );
};

const HomePage = () => {
  const { cardName } = useParams();
  const [persistentData, setPersistentData] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  // Usiamo un REF per i dati: il REF sopravvive a qualsiasi re-render della pagina
  const lastValidData = useRef(null);
  const lastSearchedCard = useRef(null);

  const { mutate, isError, error, reset } = useMutation({
    mutationFn: ({ queryData, token }) => postSearchQuery(queryData, token),
    onMutate: () => {
      setIsSearching(true);
    },
    onSuccess: (newData) => {
      lastValidData.current = newData;
      setIsSearching(false);
      setPersistentData(newData);
    },
    onError: () => setIsSearching(false)
  });

  useEffect(() => {
    if (cardName) {
      const decodedName = decodeURIComponent(cardName);
      if (lastSearchedCard.current === decodedName) return;
      lastSearchedCard.current = decodedName;
      mutate({
        queryData:{ name_card: decodedName, filters: {} }
      });
    }
  }, [cardName, mutate]);

  return (
    <>
      <SearchForm
        onSearch={(queryData) => mutate({ queryData})}
        isPending={isSearching}
        onResetResults={() => {
          lastValidData.current = null;
          setPersistentData(null);
          reset();
          setIsSearching(false);
          lastSearchedCard.current = null;
        }}
        initialCardName={cardName ? decodeURIComponent(cardName) : ""}
      />

      <Divider sx={{ my: 4, borderColor: "#333" }} />

      <ImageGallery
        images={lastValidData.current?.cards || []}
        position={lastValidData.current?.pos}
        isPending={isSearching}
        isError={isError}
        error={error}
        isSuccess={persistentData !== null}
      />
    </>
  );
};
const TermsPage = () => {
  const navigate = useNavigate();
  return (
    <Container sx={{ py: 4 }}>
      <Button variant="outlined" onClick={() => navigate('/')} sx={{ mb: 2, color: "#b88aff", borderColor: "#5c4eb9", '&:hover': { borderColor: "#b88aff" } }}>← Return to Search</Button>
      <Typography variant="h4" gutterBottom sx={{ color: "#b88aff", fontWeight: 'bold' }}>Terms and Conditions</Typography>
      <Typography variant="body2" sx={{ color: "#b88aff", opacity: 0.8, mb: 3 }}>Last updated: February 11, 2026</Typography>
      <Box sx={{ color: "#f2f2f2", display: 'flex', flexDirection: 'column', gap: 3 }}>
        <section><Typography variant="h6" sx={{ color: "#b88aff" }}>1. Acceptance of Terms</Typography><Typography paragraph>AetherSearch is unofficial Fan Content permitted under the Fan Content Policy. Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast. ©Wizards of the Coast LLC.</Typography></section>
        <Divider sx={{ borderColor: "rgba(184, 138, 255, 0.2)" }} />
        <section><Typography variant="h6" sx={{ color: "#b88aff" }}>2. AI-Generated Content Disclaimer</Typography><Typography paragraph>Results are generated automatically and may contain inaccuracies or hallucinations.</Typography></section>
      </Box>
    </Container>
  );
};

const ContactPage = () => {
  const navigate = useNavigate();
  return (
    <Container sx={{ py: 4, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 4 }}><Button variant="outlined" onClick={() => navigate('/')} sx={{ color: "#b88aff", borderColor: "#5c4eb9" }}>← Return to Search</Button></Box>
      <Typography variant="h4" gutterBottom sx={{ color: "#b88aff", fontWeight: 'bold' }}>Get in Touch</Typography>
      <Box sx={{ mt: 6, p: 4, bgcolor: "rgba(92, 78, 185, 0.1)", borderRadius: 2, border: "1px dashed #5c4eb9" }}>
        <MailOutlineIcon sx={{ fontSize: 48, color: "#b88aff", mb: 2 }} />
        <Typography variant="h6" sx={{ color: "#f2f2f2", mb: 2 }}>Have feedback?</Typography>
        <MuiLink href="mailto:mtgsimilarcards@gmail.com" sx={{ color: "#b88aff", fontWeight: "bold", fontSize: "1.2rem" }}>mtgsimilarcards@gmail.com</MuiLink>
      </Box>
    </Container>
  );
};

const PrivacyPage = () => {
  const navigate = useNavigate();
  return (
    <Container sx={{ py: 4 }}>
      <Button variant="outlined" onClick={() => navigate('/')} sx={{ mb: 4, color: "#b88aff", borderColor: "#5c4eb9" }}>← Return to Search</Button>
      <Typography variant="h3" gutterBottom sx={{ color: "#b88aff", fontWeight: 'bold' }}>Privacy Policy</Typography>
      <Box sx={{ mt: 3, color: "#f2f2f2" }}>
        <Typography variant="h6" sx={{ color: "#b88aff" }}>1. Data Collection</Typography>
        <Box sx={{ p: 3, color: '#ccc' }}>
          <Typography variant="h5" sx={{ color: '#8871ed', mb: 2 }}>
            Privacy & Affiliate Disclosure
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1.1rem', mt: 3, mb: 1 }}>
            Data Collection
          </Typography>
          <Typography paragraph sx={{ color: '#aaa' }}>
            AetherSearch respects your privacy. We do not collect personal data from our users.
            We only monitor search queries (card names) to improve our service, without keeping
            any record or trace of the individual user who performed the search.
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1.1rem', mt: 3, mb: 1 }}>
            Affiliate Program Disclosure
          </Typography>
          <Typography paragraph sx={{ color: '#aaa' }}>
            AetherSearch is a participant in the TCGplayer Affiliate Program via Impact.com.
            If you click on a card link and make a purchase, we may receive a small commission
            at no additional cost to you. These commissions help us maintain and develop the
            platform for the community.
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1.1rem', mt: 3, mb: 1 }}>
            Third-Party Tracking and Cookies
          </Typography>
          <Typography paragraph sx={{ color: '#aaa' }}>
            When you click on a product link, a tracking cookie may be placed on your device
            by our partner, <strong>Impact Tech, Inc. (Impact.com)</strong>, on behalf of
            <strong> TCGplayer</strong>. This cookie is used solely to attribute potential
            purchases to our website. These third parties may collect technical information
            such as your IP address, browser type, and purchase details. You can manage or
            disable these cookies at any time through your browser settings.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#000' }}>

        <NavigationHeader />

        <Box sx={{ flexGrow: 1, backgroundColor: "#111" }}>
          <Container maxWidth={false} sx={{ py: 4 }}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/to/:cardName" element={<HomePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contactus" element={<ContactPage />} />
              <Route path="/glossary" element={<Glossary />} />
            </Routes>
          </Container>
        </Box>

        {/* FOOTER */}
        <Box component="footer" sx={{ py: 4, mt: 'auto', borderTop: "1px solid #333", backgroundColor: "#000" }}>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2, alignItems: "center" }}>
            <IconButton component="a" href="https://www.instagram.com/aethersearch/" target="_blank" sx={{ color: '#8871ed' }}><InstagramIcon /></IconButton>
            <Typography sx={{ color: '#8871ed' }}>Follow AetherSearch on Instagram</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <MuiLink component={Link} to="/about" sx={{ color: "#aaa", textDecoration: "none", "&:hover": { color: "#b88aff" } }}>About Us</MuiLink>
            <Typography sx={{ color: "#333" }}>|</Typography>
            <MuiLink component={Link} to="/terms" sx={{ color: "#aaa", textDecoration: "none", "&:hover": { color: "#b88aff" } }}>Terms</MuiLink>
            <Typography sx={{ color: "#333" }}>|</Typography>
            <MuiLink component={Link} to="/contactus" sx={{ color: "#aaa", textDecoration: "none", "&:hover": { color: "#b88aff" } }}>ContactUs</MuiLink>
            <Typography sx={{ color: "#333" }}>|</Typography>
            <MuiLink component={Link} to="/privacy" sx={{ color: "#aaa", textDecoration: "none", "&:hover": { color: "#b88aff" } }}>Privacy</MuiLink>
          </Box>
          <Typography variant="caption" display="block" align="center" sx={{ color: "#666", px: 2 }}>
            AetherSearch on MTGSimilarCards.com is unofficial Fan Content permitted under the Fan Content Policy. Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast. ©Wizards of the Coast LLC.
          </Typography>
          <Typography variant="h1" sx={{ color: "transparent", fontWeight: "light", mt: 1, fontSize: "0.1rem", lineHeight: 0.1 }}>
            Search any Magic: The Gathering card to find replacements, upgrades, and cards that play a similar role in your deck.
            Best MTG Search Engine: Find Similar, Alternative & Replacement Cards for Your Magic: The Gathering Decks
            Discover similar Magic: The Gathering cards. Find replacements, upgrades, and cards that play the same role in MTG deckbuilding.
          </Typography>
        </Box>
      </Box>
    </BrowserRouter>
  );
};

export default App;